import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@entry/navigation";

import { Anchor } from "@components/Anchor";
import { ErrorComponent } from "@components/ErrorComponent";
import { TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { VERIFIED_TERMS_OF_USE_URL } from "@constants";
import { Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useOnboarding, useSetUserIdentity, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { formatPhoneNumber, sanitizePhoneNumber } from "@utils";
import { validationSchema } from "./content";
import { PhoneNumberRouteFormValues } from "./types";

export const PhoneNumberRoute = () => {
  const { showErrorBanner } = useErrorBanner();

  const { user, loading: userLoading, error: userError } = useUser();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<PhoneNumberRouteFormValues>({
    defaultValues: {
      phoneNumber: formatPhoneNumber(user?.phoneNumber ?? ""),
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [loading, setLoading] = useState(false);

  const [setUserIdentity] = useSetUserIdentity();

  const navigate = useNavigate();
  const { resetToNext } = useOnboarding();

  const phoneNumberController = register("phoneNumber");

  const handleSubmit = async ({ phoneNumber: phoneNumberValue }: PhoneNumberRouteFormValues) => {
    const phoneNumber = sanitizePhoneNumber(phoneNumberValue);

    try {
      setLoading(true);

      const { data } = await setUserIdentity({
        variables: {
          phoneNumber,
        },
      });

      if (data?.setUserIdentity?.phoneNumber) {
        if (data.setUserIdentity?.phoneNumberVerified) {
          resetToNext();
        } else {
          navigate(Routes.PhoneVerification);
        }
      } else {
        showErrorBanner();
      }
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.PhoneNumberRoutePhoneNumberTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.SignUpRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
  };

  const onChangePhoneNumber = (value: string) => {
    const formattedPhoneNumber = formatPhoneNumber(value);

    setValue("phoneNumber", formattedPhoneNumber, constants.form);
  };

  if (userLoading) return <Loader />;
  if (userError) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...phoneNumberController}
        label={i18n.t(Translations.PhoneNumberRoutePhoneNumberLabel)}
        placeholder={i18n.t(Translations.PhoneNumberRoutePhoneNumberPlaceholder)}
        value={watch("phoneNumber")}
        onChangeText={onChangePhoneNumber}
        error={errors.phoneNumber?.message}
        disabled={Boolean(user?.phoneNumberVerified)}
      />
      <Spacing size={SpacingSizes.M} />

      <Caption style={{ textAlign: "center" }}>
        {i18n.t(Translations.VerifiedFooter1)}
        <Anchor href={VERIFIED_TERMS_OF_USE_URL}>
          {i18n.t(Translations.VerifiedFooterHighlight)}
        </Anchor>
        {i18n.t(Translations.VerifiedFooter2)}
      </Caption>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
